import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserPlus, Pencil, Ban, Search, ShieldCheck, RotateCcw, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
    admin: 'Administrador', pilot: 'Piloto', dispatcher: 'Despachador',
    maintenance: 'Mantenimiento', ccm: 'CCM', cabin_crew: 'Tripulante de cabina', ccod: 'CCO', crew_admin: 'Admin Tripulaciones',
    counter: 'Mostrador', ramp: 'Rampa', comercial: 'Comercial', ventas: 'Ventas',
    gerencia: 'Gerencia', analista: 'Analista', calidad: 'Calidad',
    gh_supervisor: 'Supervisor GH', gh_leader: 'Lider GH',
};

const CREATABLE_ROLES = ['admin', 'pilot', 'dispatcher', 'maintenance', 'ccm', 'ccod', 'crew_admin', 'counter', 'ramp', 'comercial', 'ventas', 'gerencia', 'analista', 'calidad'];

const EMPTY_FORM = { email: '', password: '', first_name: '', last_name: '', role: 'pilot', employee_id: '', license_number: '', phone: '' };

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [createOpen, setCreateOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    useEffect(() => { fetchUsers(); }, []);

    const fetchUsers = async () => {
        try {
            const r = await axios.get(`${API}/users`);
            setUsers(r.data);
        } catch (e) {
            toast.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditingUser(null);
        setForm(EMPTY_FORM);
        setCreateOpen(true);
    };

    const openEdit = (u) => {
        setEditingUser(u);
        setForm({
            email: u.email || '', password: '',
            first_name: u.first_name || '', last_name: u.last_name || '',
            role: u.role || 'pilot', employee_id: u.employee_id || '',
            license_number: u.license_number || '', phone: u.phone || '',
        });
        setCreateOpen(true);
    };

    const handleResendInvite = async (u) => {
        try {
            await axios.post(`${API}/users/${u.id}/resend-invitation`);
            toast.success(`Invitación reenviada a ${u.email}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo reenviar la invitación');
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            if (editingUser) {
                const { password, email, ...updateFields } = form;
                await axios.put(`${API}/users/${editingUser.id}`, updateFields);
                toast.success('Usuario actualizado');
            } else {
                const { password, ...invite } = form;
                await axios.post(`${API}/auth/register`, invite);
                toast.success(`Invitación enviada a ${form.email}`);
            }
            setCreateOpen(false);
            fetchUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar usuario');
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async (u) => {
        if (!window.confirm(`Desactivar a ${u.first_name} ${u.last_name}?`)) return;
        try {
            await axios.delete(`${API}/users/${u.id}`);
            toast.success('Usuario desactivado');
            fetchUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al desactivar');
        }
    };

    // Auditoria 3 - Item 7: reactivar usuario inactivo
    const handleReactivate = async (u) => {
        if (!window.confirm(`Reactivar a ${u.first_name} ${u.last_name}? Podra iniciar sesion nuevamente.`)) return;
        try {
            await axios.put(`${API}/users/${u.id}`, { is_active: true });
            toast.success('Usuario reactivado');
            fetchUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al reactivar');
        }
    };

    const filtered = users.filter(u => {
        if (roleFilter !== 'all' && u.role !== roleFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            const hay = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    });

    return (
        <div className="max-w-7xl mx-auto space-y-6" data-testid="users-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">Mi Equipo</h1>
                        <p className="text-muted-foreground">Gestion de usuarios y accesos de la aerolinea</p>
                    </div>
                    <Button onClick={openCreate} data-testid="users-create-btn">
                        <UserPlus className="w-4 h-4 mr-2" /> Nuevo Usuario
                    </Button>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                        <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> {filtered.length} usuario(s)</CardTitle>
                        <div className="flex gap-2">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                                <Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 w-64" data-testid="users-search" />
                            </div>
                            <Select value={roleFilter} onValueChange={setRoleFilter}>
                                <SelectTrigger className="w-48" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los roles</SelectItem>
                                    {CREATABLE_ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="text-center py-8 text-muted-foreground">Cargando...</div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Nombre</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Rol</TableHead>
                                        <TableHead>Legajo</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(u => (
                                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                            <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                                            <TableCell>{u.email}</TableCell>
                                            <TableCell><Badge variant="outline">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                                            <TableCell>{u.employee_id || '-'}</TableCell>
                                            <TableCell>
                                                {u.is_active === false || u.active === false ? (
                                                    <Badge variant="destructive">Inactivo</Badge>
                                                ) : u.pending_activation ? (
                                                    <Badge variant="outline" className="text-amber-500 border-amber-500/40" data-testid={`user-pending-${u.id}`}>Invitación pendiente</Badge>
                                                ) : (
                                                    <Badge>Activo</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right space-x-2">
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                {u.is_active !== false && u.active !== false && (
                                                    <Button variant="ghost" size="sm" onClick={() => handleDeactivate(u)} data-testid={`user-deactivate-${u.id}`}>
                                                        <Ban className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                )}
                                                {u.pending_activation && u.active !== false && (
                                                    <Button variant="ghost" size="sm" onClick={() => handleResendInvite(u)} data-testid={`user-resend-invite-${u.id}`} title="Reenviar invitación">
                                                        <Mail className="w-4 h-4 text-amber-500" />
                                                    </Button>
                                                )}
                                                {(u.is_active === false || u.active === false) && (
                                                    <Button variant="ghost" size="sm" onClick={() => handleReactivate(u)} data-testid={`user-reactivate-${u.id}`} title="Reactivar usuario">
                                                        <RotateCcw className="w-4 h-4 text-emerald-500" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filtered.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="users-empty-state">
                                                No hay usuarios que coincidan. Invita a tu equipo con "Nuevo Usuario".
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogContent className="sm:max-w-lg" data-testid="users-form-dialog">
                        <DialogHeader>
                            <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
                            <DialogDescription>
                                {editingUser
                                    ? 'Actualiza los datos del usuario. La contrasena no puede modificarse desde aqui.'
                                    : 'Completa los datos e invitaremos al usuario por correo para que active su cuenta y elija su contraseña.'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Nombre</Label>
                                    <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="users-first-name" />
                                </div>
                                <div>
                                    <Label>Apellido</Label>
                                    <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="users-last-name" />
                                </div>
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!!editingUser} data-testid="users-email" />
                            </div>
                            {!editingUser && (
                                <p className="text-xs text-muted-foreground" data-testid="users-invite-hint">El usuario recibirá un correo para activar su cuenta y elegir su propia contraseña.</p>
                            )}
                            <div>
                                <Label>Rol</Label>
                                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                                    <SelectTrigger data-testid="users-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {CREATABLE_ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Legajo</Label>
                                    <Input value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Licencia</Label>
                                    <Input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <Label>Telefono</Label>
                                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
                            <Button onClick={handleSave} disabled={saving} data-testid="users-save-btn">
                                {saving ? 'Guardando...' : (editingUser ? 'Guardar cambios' : 'Crear usuario')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
    );
}
